import { useAppContext } from "@shared/context/AppContext";
import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
  useCallback,
} from "react";

interface SidebarContextProps {
  isSidebarDropdownOpened: boolean;
  setIsSidebarDropdownOpened: (isOpen: boolean) => void;
  refreshProjects: () => void;
  refreshChats: () => void;
  refreshPrograms: () => void;
  refreshTrigger: number;
  refreshChatsTrigger: number;
  refreshProgramsTrigger: number;
  sidebarDisabled: boolean;
  setSidebarDisabled: (disabled: boolean) => void;
}

const SidebarContext = createContext<SidebarContextProps | undefined>(
  undefined
);

export const SidebarProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isSidebarDropdownOpened, setIsSidebarDropdownOpened] =
    useState<boolean>(() => {
      const savedState = localStorage.getItem("isSidebarDropdownOpened");
      return savedState ? JSON.parse(savedState) : false;
    });

  const [sidebarDisabled, setSidebarDisabled] = useState<boolean>(false);

  useEffect(() => {
    localStorage.setItem(
      "isSidebarDropdownOpened",
      JSON.stringify(isSidebarDropdownOpened)
    );
  }, [isSidebarDropdownOpened]);

  const {
    refreshChatsTrigger,
    setRefreshChatsTrigger,
    refreshProgramsTrigger,
    setRefreshProgramsTrigger,
    refreshTrigger,
    setRefreshTrigger,
  } = useAppContext();

  // Function to trigger Sidebar project list refresh
  const refreshProjects = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1); // Incrementing state forces Sidebar to refresh
  }, []);

  const refreshChats = useCallback(() => {
    setRefreshChatsTrigger((prev) => prev + 1); // Incrementing state forces Sidebar to refresh
  }, []);

  const refreshPrograms = useCallback(() => {
    setRefreshProgramsTrigger((prev) => prev + 1); // Incrementing state forces Sidebar to refresh
  }, []);

  return (
    <SidebarContext.Provider
      value={{
        isSidebarDropdownOpened,
        setIsSidebarDropdownOpened,
        sidebarDisabled,
        setSidebarDisabled,
        refreshProjects,
        refreshTrigger,
        refreshChatsTrigger,
        refreshChats,
        refreshProgramsTrigger,
        refreshPrograms,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = (): SidebarContextProps => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
};
